import { useAuth0 } from "@auth0/auth0-react";
import { Box, Grid, MenuItem } from "@mui/material";
import { AutocompleteInput, Button, PageLoader } from "common/components";
import { APP_ROUTES } from "common/constants/ROUTES";
import { colors } from "common/scss/themes";
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useGetFirmsAutompleteQuery,
  useGetFirmsQuery,
  useImpersonateFirmMutation
} from "services/impersonate/impersonateApi";
import { Firm } from "services/impersonate/models/firm.interface";
import { getUserData } from "services/user/userSlice";
import { useAppDispatch } from "stateManagement/hooks";
import { clearAllApiCaches } from "../../../utils/cacheUtils";

interface ImpersonateProps {
  readonly handleModal: (value: boolean) => void;
}

const v2Enabled = process.env.REACT_APP_V2_ENABLED === "true";

function Impersonate({ handleModal }: ImpersonateProps) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { getAccessTokenSilently } = useAuth0();

  const [selectedOption, setSelectedOption] = useState<Firm | null>(null);
  const [inputValue, setInputValue] = useState("");

  const { data: firmsV2, isLoading: isLoadingV2 } = useGetFirmsAutompleteQuery(
    inputValue,
    { skip: !v2Enabled || !inputValue }
  );

  const { data: firmsV1, isLoading: isLoadingV1 } = useGetFirmsQuery(
    undefined,
    {
      skip: v2Enabled
    }
  );

  const [impersonateFirmQuery] = useImpersonateFirmMutation();

  const firms = useMemo(() => {
    const list = v2Enabled ? firmsV2 : firmsV1;
    return Array.isArray(list) ? list : [];
  }, [v2Enabled, firmsV2, firmsV1]);

  const isLoading = v2Enabled ? isLoadingV2 : isLoadingV1;

  const handleConfirmClick = async () => {
    if (!selectedOption) return;

    await impersonateFirmQuery({
      id: selectedOption.id,
      firm: selectedOption
    });

    // ✅ Clear all RTK Query caches to prevent stale data from previous firm
    clearAllApiCaches(dispatch);

    // ✅ Refetch /me and update Redux
    const token = await getAccessTokenSilently();
    await dispatch(getUserData({ accessToken: token }));

    navigate(APP_ROUTES.FIRM);
    handleModal(false);
  };

  return (
    <Grid container alignItems="center" spacing={2} py={2}>
      <Grid item xs={12}>
        <Box position="relative">
          <AutocompleteInput
            placeholder="Select or search for a firm"
            options={firms}
            value={selectedOption}
            onChange={(event: any, newValue: any) => {
              setSelectedOption(newValue);
            }}
            renderOption={(props, option) => (
              <MenuItem
                {...props}
                key={option.id}
                value={option.id}
                className="h-[36px] min-h-[36px] p-2 font-sans text-sm font-medium text-gray-10 hover:text-primary-8 hover:bg-primary-1"
                sx={{
                  "&.Mui-selected": {
                    color: colors.primary[8],
                    backgroundColor: "white",
                    "&:hover, &.Mui-focusVisible": {
                      backgroundColor: colors.primary[1]
                    }
                  }
                }}
              >
                {option.firm}
              </MenuItem>
            )}
            inputValue={inputValue}
            onInputChange={(event, newInputValue) => {
              setInputValue(newInputValue);
            }}
            getOptionLabel={(option) => option.firm || ""}
          />
          {isLoading && (
            <Box
              position="absolute"
              right={10}
              top="50%"
              transform="translateY(-50%)"
            >
              <PageLoader size="sm" />
            </Box>
          )}
        </Box>
      </Grid>
      <Grid item xs={12}>
        <Box display="flex" justifyContent="flex-end">
          <Button
            id="select-firm"
            variant="primary"
            disabled={!selectedOption}
            onClick={handleConfirmClick}
            message="Select Firm"
            size="md"
          />
        </Box>
      </Grid>
    </Grid>
  );
}

export default Impersonate;
